import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Snackbar,
  Tooltip,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import CategoryIcon from '@mui/icons-material/Category';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import LockIcon from '@mui/icons-material/Lock';
import { Link as RouterLink } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { getApps, createApp, updateApp, deleteApp, getCategories, createCategory, verifyAdmin, setAdminToken, getAdminToken, clearAdminToken } from '../services/api';

const emptyApp = {
  name: '',
  shortName: '',
  description: '',
  icon: 'apps',
  category: '',
  url: '',
  technicalOwner: { name: '', email: '', phone: '' },
  operationalOwner: { name: '', email: '', phone: '' },
  status: 'development',
  version: '1.0.0',
  technologies: [],
  userGuideUrl: '',
  requirementsUrl: '',
  supportEmail: '',
  featured: false,
};

const iconOptions = [
  { value: 'inventory', label: 'Inventario' },
  { value: 'security', label: 'Seguridad' },
  { value: 'support_agent', label: 'Soporte' },
  { value: 'apps', label: 'Aplicaciones' },
];

const statusOptions = [
  { value: 'production', label: 'Producción' },
  { value: 'development', label: 'Desarrollo' },
  { value: 'beta', label: 'Beta' },
  { value: 'deprecated', label: 'Descontinuada' },
];

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(Boolean(getAdminToken()));
  const [accessCode, setAccessCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [authError, setAuthError] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [apps, setApps] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [currentApp, setCurrentApp] = useState(emptyApp);
  const [appToDelete, setAppToDelete] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [techInput, setTechInput] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (isAuthenticated) {
      fetchApps();
      fetchCategories();
    }
  }, [isAuthenticated]);

  const handleAccessSubmit = async () => {
    if (!accessCode) {
      setAuthError(true);
      return;
    }
    setVerifying(true);
    try {
      await verifyAdmin(accessCode);
      setAdminToken(accessCode);
      setIsAuthenticated(true);
      setAuthError(false);
      setAccessCode('');
    } catch (err) {
      clearAdminToken();
      setAuthError(true);
    } finally {
      setVerifying(false);
    }
  };

  const fetchApps = async () => {
    try {
      setLoading(true);
      const data = await getApps();
      setApps(data);
    } catch (error) {
      showSnackbar('Error al cargar las aplicaciones', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const data = await getCategories();
      setCategories(data);
    } catch (error) {
      showSnackbar('Error al cargar las categorías', 'error');
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      await createCategory({ name: newCategoryName.trim() });
      showSnackbar('Categoría creada correctamente');
      setCategoryDialogOpen(false);
      setNewCategoryName('');
      fetchCategories();
    } catch (error) {
      showSnackbar('Error al crear la categoría', 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleOpenDialog = (app = null) => {
    if (app) {
      setCurrentApp(app);
      setIsEditing(true);
    } else {
      setCurrentApp(emptyApp);
      setIsEditing(false);
    }
    setTechInput('');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setCurrentApp(emptyApp);
    setTechInput('');
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setCurrentApp(prev => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: value },
      }));
    } else {
      setCurrentApp(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleAddTechnology = () => {
    if (techInput.trim() && !currentApp.technologies.includes(techInput.trim())) {
      setCurrentApp(prev => ({
        ...prev,
        technologies: [...prev.technologies, techInput.trim()],
      }));
      setTechInput('');
    }
  };

  const handleRemoveTechnology = (tech) => {
    setCurrentApp(prev => ({
      ...prev,
      technologies: prev.technologies.filter(t => t !== tech),
    }));
  };

  const handleSave = async () => {
    try {
      if (isEditing) {
        await updateApp(currentApp.id, currentApp);
        showSnackbar('Aplicación actualizada correctamente');
      } else {
        await createApp(currentApp);
        showSnackbar('Aplicación creada correctamente');
      }
      handleCloseDialog();
      fetchApps();
    } catch (error) {
      showSnackbar('Error al guardar la aplicación', 'error');
    }
  };

  const handleDeleteClick = (app) => {
    setAppToDelete(app);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteApp(appToDelete.id);
      showSnackbar('Aplicación eliminada correctamente');
      setDeleteDialogOpen(false);
      setAppToDelete(null);
      fetchApps();
    } catch (error) {
      showSnackbar('Error al eliminar la aplicación', 'error');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      production: '#24a148',
      development: '#f1c21b',
      beta: '#a56eff',
      deprecated: '#fa4d56',
    };
    return colors[status] || '#6f6f6f';
  };

  // Auth screen
  if (!isAuthenticated) {
    return (
      <Box
        sx={{
          minHeight: 'calc(100vh - 48px)',
          backgroundColor: '#f4f4f4',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Paper
          sx={{
            p: 4,
            maxWidth: 400,
            width: '100%',
            borderRadius: '16px',
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              backgroundColor: '#0f62fe15',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <LockIcon sx={{ fontSize: 28, color: '#0f62fe' }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
            Acceso Restringido
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Ingresa el código de acceso para continuar
          </Typography>
          <TextField
            fullWidth
            type={showPassword ? 'text' : 'password'}
            placeholder="Código de administrador"
            value={accessCode}
            onChange={(e) => {
              setAccessCode(e.target.value);
              setAuthError(false);
            }}
            onKeyPress={(e) => e.key === 'Enter' && handleAccessSubmit()}
            error={authError}
            helperText={authError ? 'Código incorrecto' : ''}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
              },
            }}
          />
          <Button
            fullWidth
            variant="contained"
            onClick={handleAccessSubmit}
            disabled={!accessCode || verifying}
            sx={{
              backgroundColor: '#0f62fe',
              borderRadius: '8px',
              py: 1.2,
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': { backgroundColor: '#0043ce' },
            }}
          >
            Ingresar
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: 'calc(100vh - 48px)', backgroundColor: '#f4f4f4' }}>
      {/* Header */}
      <Box
        sx={{
          backgroundColor: 'white',
          borderBottom: '1px solid #e0e0e0',
          py: 1.5,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                Administración
              </Typography>
              <Chip
                label={`${apps.length} apps`}
                size="small"
                sx={{
                  height: 24,
                  backgroundColor: '#0f62fe',
                  color: 'white',
                  fontWeight: 500,
                  fontSize: '0.75rem',
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                component={RouterLink}
                to="/admin/costos"
                variant="outlined"
                size="small"
                startIcon={<AttachMoneyIcon />}
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  borderColor: '#0f62fe',
                  color: '#0f62fe',
                }}
              >
                Costos
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                sx={{
                  backgroundColor: '#0f62fe',
                  borderRadius: '8px',
                  textTransform: 'none',
                  '&:hover': { backgroundColor: '#0043ce' },
                }}
              >
                Nueva App
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="lg" sx={{ py: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: 'none',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f4f4f4' }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Nombre</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Categoría</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Estado</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Versión</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Responsable</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }} align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {apps.map((app) => (
                  <TableRow key={app.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
                        {app.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#525252' }}>
                        {app.category}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          px: 1,
                          py: 0.25,
                          borderRadius: '12px',
                          backgroundColor: `${getStatusColor(app.status)}15`,
                        }}
                      >
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            backgroundColor: getStatusColor(app.status),
                          }}
                        />
                        <Typography variant="caption" sx={{ fontSize: '0.7rem', color: getStatusColor(app.status) }}>
                          {statusOptions.find(s => s.value === app.status)?.label}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#525252' }}>
                        {app.version}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#525252' }}>
                        {app.technicalOwner?.name}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => handleOpenDialog(app)} sx={{ color: '#0f62fe' }}>
                          <EditIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton size="small" onClick={() => handleDeleteClick(app)} sx={{ color: '#da1e28' }}>
                          <DeleteIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>

      {/* Edit/Create Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px' } }}
      >
        <Box sx={{ backgroundColor: '#161616', color: 'white', py: 1.5, px: 2.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: '0.95rem' }}>
            {isEditing ? 'Editar Aplicación' : 'Nueva Aplicación'}
          </Typography>
        </Box>
        <DialogContent sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Row 1 */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField fullWidth size="small" label="Nombre" value={currentApp.name} onChange={(e) => handleInputChange('name', e.target.value)} />
              <TextField sx={{ width: 120 }} size="small" label="Sigla" value={currentApp.shortName} onChange={(e) => handleInputChange('shortName', e.target.value)} />
            </Box>
            {/* Row 2 */}
            <TextField fullWidth size="small" label="Descripción" multiline rows={2} value={currentApp.description} onChange={(e) => handleInputChange('description', e.target.value)} />
            {/* Row 3 */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField fullWidth size="small" label="URL App" value={currentApp.url} onChange={(e) => handleInputChange('url', e.target.value)} />
              <TextField fullWidth size="small" label="URL Docs" value={currentApp.userGuideUrl} onChange={(e) => handleInputChange('userGuideUrl', e.target.value)} />
              <TextField fullWidth size="small" label="URL Requerimientos" value={currentApp.requirementsUrl || ''} onChange={(e) => handleInputChange('requirementsUrl', e.target.value)} />
            </Box>
            {/* Row 4 */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>Icono</InputLabel>
                <Select value={currentApp.icon} label="Icono" onChange={(e) => handleInputChange('icon', e.target.value)}>
                  {iconOptions.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ flexGrow: 1 }}>
                <InputLabel>Categoría</InputLabel>
                <Select
                  value={currentApp.category}
                  label="Categoría"
                  onChange={(e) => {
                    if (e.target.value === '__new__') {
                      setCategoryDialogOpen(true);
                    } else {
                      handleInputChange('category', e.target.value);
                    }
                  }}
                >
                  {categories.map(cat => (
                    <MenuItem key={cat.id} value={cat.name}>{cat.name}</MenuItem>
                  ))}
                  <MenuItem value="__new__" sx={{ color: '#0f62fe', fontWeight: 500, borderTop: '1px solid #e0e0e0', mt: 1 }}>
                    <AddIcon sx={{ fontSize: 16, mr: 1 }} /> Nueva categoría...
                  </MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Estado</InputLabel>
                <Select value={currentApp.status} label="Estado" onChange={(e) => handleInputChange('status', e.target.value)}>
                  {statusOptions.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField sx={{ width: 100 }} size="small" label="Versión" value={currentApp.version} onChange={(e) => handleInputChange('version', e.target.value)} />
            </Box>
            {/* Technologies */}
            <Box>
              <Typography variant="caption" sx={{ color: '#0f62fe', fontWeight: 600, mb: 1, display: 'block' }}>
                Tecnologías
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <TextField
                  size="small"
                  placeholder="Agregar..."
                  value={techInput}
                  onChange={(e) => setTechInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddTechnology()}
                  sx={{ width: 150 }}
                />
                <Button variant="outlined" size="small" onClick={handleAddTechnology} sx={{ minWidth: 'auto', px: 2 }}>+</Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {currentApp.technologies.map((tech, i) => (
                  <Chip key={i} label={tech} size="small" onDelete={() => handleRemoveTechnology(tech)} sx={{ height: 24, fontSize: '0.7rem' }} />
                ))}
              </Box>
            </Box>
            {/* Technical Owner */}
            <Box>
              <Typography variant="caption" sx={{ color: '#0f62fe', fontWeight: 600, mb: 1, display: 'block' }}>
                Responsable Técnico
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField fullWidth size="small" label="Nombre" value={currentApp.technicalOwner?.name || ''} onChange={(e) => handleInputChange('technicalOwner.name', e.target.value)} />
                <TextField fullWidth size="small" label="Email" value={currentApp.technicalOwner?.email || ''} onChange={(e) => handleInputChange('technicalOwner.email', e.target.value)} />
                <TextField sx={{ width: 150 }} size="small" label="Teléfono" value={currentApp.technicalOwner?.phone || ''} onChange={(e) => handleInputChange('technicalOwner.phone', e.target.value)} />
              </Box>
            </Box>
            {/* Operational Owner */}
            <Box>
              <Typography variant="caption" sx={{ color: '#0f62fe', fontWeight: 600, mb: 1, display: 'block' }}>
                Responsable Operativo
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField fullWidth size="small" label="Nombre" value={currentApp.operationalOwner?.name || ''} onChange={(e) => handleInputChange('operationalOwner.name', e.target.value)} />
                <TextField fullWidth size="small" label="Email" value={currentApp.operationalOwner?.email || ''} onChange={(e) => handleInputChange('operationalOwner.email', e.target.value)} />
                <TextField sx={{ width: 150 }} size="small" label="Teléfono" value={currentApp.operationalOwner?.phone || ''} onChange={(e) => handleInputChange('operationalOwner.phone', e.target.value)} />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={handleCloseDialog} startIcon={<CloseIcon />} sx={{ color: '#525252' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            startIcon={<SaveIcon />}
            sx={{ backgroundColor: '#0f62fe', borderRadius: '8px' }}
          >
            {isEditing ? 'Guardar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} PaperProps={{ sx: { borderRadius: '12px' } }}>
        <Box sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Eliminar aplicación
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            ¿Eliminar <strong>{appToDelete?.name}</strong>? Esta acción no se puede deshacer.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: '#525252' }}>Cancelar</Button>
            <Button variant="contained" color="error" onClick={handleDeleteConfirm} sx={{ borderRadius: '8px' }}>
              Eliminar
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={categoryDialogOpen} onClose={() => setCategoryDialogOpen(false)} PaperProps={{ sx: { borderRadius: '12px' } }}>
        <Box sx={{ p: 3, minWidth: 300 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CategoryIcon sx={{ color: '#0f62fe' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Nueva Categoría
            </Typography>
          </Box>
          <TextField
            fullWidth
            size="small"
            label="Nombre de la categoría"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleCreateCategory()}
            autoFocus
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button onClick={() => { setCategoryDialogOpen(false); setNewCategoryName(''); }} sx={{ color: '#525252' }}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateCategory}
              disabled={!newCategoryName.trim()}
              sx={{ backgroundColor: '#0f62fe', borderRadius: '8px' }}
            >
              Crear
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} sx={{ borderRadius: '8px' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Admin;
